import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { getLivePrice, getTradeLock, getRowImage } from '../components/helpers';
import { isFlipRow } from '../components/investments';
import {
  Page, Hero, StatStrip, ControlRail, Sheet, OptionList, Empty, Fab,
  Tag, Delta, useReveal, MoreButton,
  COND_ABBR, getItemTypes, money, signed, pct, compactMoney, dateShort,
} from './mobileShared';

// Armory — everything currently held. Two-column card grid so a skin render
// stays large enough to recognise at a glance, which is the whole point of
// this tab; the numbers sit underneath in a fixed reading order.

const SORTS = [
  { key: 'value',    label: 'Highest cost' },
  { key: 'delta',    label: 'Biggest gain' },
  { key: 'deltaAsc', label: 'Biggest loss' },
  { key: 'newest',   label: 'Newest first' },
  { key: 'name',     label: 'Name A–Z' },
];

export default function MobileLive({
  rows, settings, livePrices, skinsData = [], inventoryMap, onEdit,
  onAddItem, onBulkAdd, onImportInventory, onExportCSV,
}) {
  const [query, setQuery]   = useState('');
  const [cat, setCat]       = useState('all');
  const [sort, setSort]     = useState('value');
  const [filters, setFilters] = useState(false);
  const [actions, setActions] = useState(false);

  // Set on .mb-app so the Zone skin can swap in ink that reads on paper.
  const profC = 'var(--mb-pos)';
  const lossC = 'var(--mb-neg)';

  const holdings = useMemo(() => (rows || [])
    .map((row, realIndex) => ({ row, realIndex }))
    // Investments live in the Vault only — one item, one home.
    .filter(({ row }) => isFlipRow(row))
    .map(e => {
      const buy  = parseFloat(e.row.buyPrice) || 0;
      const mkt  = getLivePrice(livePrices, e.row)?.starting_at?.price ?? null;
      const diff = mkt != null ? mkt - buy : null;
      const type = getItemTypes(e.row)
        .map(t => settings?.itemTypes?.find(it => it.value === t.value))
        .find(Boolean);
      const inv = e.row.assetId ? inventoryMap?.get(e.row.assetId) : null;
      const { locked, until } = getTradeLock(e.row, inv);
      return { ...e, buy, mkt, diff, type, locked, until };
    }), [rows, livePrices, settings, inventoryMap]);

  const cats = useMemo(() => {
    const present = new Set();
    holdings.forEach(h => getItemTypes(h.row).forEach(t => present.add(t.value)));
    return (settings?.itemTypes || []).filter(t => present.has(t.value));
  }, [holdings, settings]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = holdings.filter(h => {
      if (q && !h.row.skin.value.toLowerCase().includes(q)) return false;
      if (cat !== 'all' && !getItemTypes(h.row).some(t => t.value === cat)) return false;
      return true;
    });
    const by = {
      value:    (a, b) => b.buy - a.buy,
      delta:    (a, b) => (b.diff ?? -Infinity) - (a.diff ?? -Infinity),
      deltaAsc: (a, b) => (a.diff ?? Infinity) - (b.diff ?? Infinity),
      newest:   (a, b) => new Date(b.row.timestamp) - new Date(a.row.timestamp),
      name:     (a, b) => a.row.skin.value.localeCompare(b.row.skin.value),
    }[sort];
    return [...list].sort(by);
  }, [holdings, query, cat, sort]);

  const totals = useMemo(() => {
    const cost = holdings.reduce((s, h) => s + h.buy, 0);
    let value = 0, unreal = 0, priced = 0;
    holdings.forEach(h => {
      if (h.mkt != null) { value += h.mkt; unreal += h.mkt - h.buy; priced++; }
      else value += h.buy;
    });
    return { cost, value, unreal, priced, locked: holdings.filter(h => h.locked).length };
  }, [holdings]);

  const roi = totals.cost > 0 ? (totals.unreal / totals.cost) * 100 : null;
  const [shown, hasMore, showMore] = useReveal(filtered, 12);
  const activeFilters = (cat !== 'all' ? 1 : 0) + (sort !== 'value' ? 1 : 0);

  return (
    <Page>
      <Hero
        label="Market value of everything held"
        value={compactMoney(totals.value)}
        tone={totals.unreal >= 0 ? 'up' : 'down'}
        sub={<>
          <span style={{ color: totals.unreal >= 0 ? profC : lossC }}>{signed(totals.unreal)}</span>
          {roi != null ? ` · ${pct(roi)} on cost` : ' · no live prices yet'}
        </>}
        foot={`${holdings.length} item${holdings.length !== 1 ? 's' : ''} · ${totals.priced} priced${totals.locked ? ` · ${totals.locked} trade locked` : ''}`}
      />

      <StatStrip items={[
        { label: 'Cost', value: money(totals.cost), sub: 'what you paid' },
        { label: 'Market value', value: money(totals.value), sub: `${totals.priced} of ${holdings.length} priced` },
        { label: 'Unrealized', value: signed(totals.unreal), color: totals.unreal >= 0 ? profC : lossC,
          sub: roi != null ? `${pct(roi)} on cost` : 'n/a' },
        { label: 'Trade locked', value: String(totals.locked), sub: 'cannot be sold yet' },
      ]} />

      <ControlRail
        query={query} onQuery={setQuery}
        placeholder="Search your items…"
        activeCount={activeFilters}
        onOpen={() => setFilters(true)}
      />

      {filtered.length === 0 ? (
        <Empty action={holdings.length === 0
          ? <button className="mb-btn primary" onClick={onAddItem}>Add your first item</button>
          : null}>
          {holdings.length === 0
            ? 'Nothing in the armory yet.'
            : 'No items match your filters.'}
        </Empty>
      ) : (
        <>
          <div className="mb-grid2">
            {shown.map((h, i) => {
              const img = getRowImage(h.row, skinsData, inventoryMap);
              const pc  = h.diff != null && h.buy > 0 ? (h.diff / h.buy) * 100 : null;
              return (
                <motion.button
                  key={h.realIndex}
                  className="mb-card tap"
                  onClick={() => onEdit && onEdit(h.row, h.realIndex)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 8) * 0.03 }}
                >
                  <div className="mb-card-stage">
                    {img ? <img src={img} alt="" loading="lazy" /> : <span className="mb-thumb-blank" />}
                    {h.locked && (
                      <span className="mb-lock">
                        {h.until ? `🔒 ${dateShort(h.until)}` : '🔒 Locked'}
                      </span>
                    )}
                  </div>
                  <div className="mb-card-name">{h.row.skin.value}</div>
                  <div className="mb-card-tags">
                    {h.row.condition?.value && (
                      <Tag>{COND_ABBR[h.row.condition.value] || h.row.condition.value}</Tag>
                    )}
                    {h.type && <Tag color={h.type.color}>{h.type.label}</Tag>}
                  </div>
                  <div className="mb-card-prices">
                    <span><i>Paid</i>{money(h.buy)}</span>
                    <span><i>Market</i>{h.mkt != null ? money(h.mkt) : 'n/a'}</span>
                  </div>
                  <Delta value={h.diff} pctValue={pc} profitColor={profC} lossColor={lossC} />
                </motion.button>
              );
            })}
          </div>
          {hasMore && <MoreButton onClick={showMore} remaining={filtered.length - shown.length} />}
        </>
      )}

      <Fab onClick={() => setActions(true)} label="Item actions" />

      <Sheet open={filters} onClose={() => setFilters(false)} title="Filter & sort"
        footer={
          <button className="mb-btn ghost" onClick={() => { setCat('all'); setSort('value'); }}>
            Reset
          </button>
        }>
        <OptionList
          label="Category"
          value={cat}
          onChange={v => { setCat(v); setFilters(false); }}
          options={[{ key: 'all', label: 'All categories' },
            ...cats.map(t => ({ key: t.value, label: t.label, color: t.color }))]}
        />
        <OptionList
          label="Sort by"
          value={sort}
          onChange={v => { setSort(v); setFilters(false); }}
          options={SORTS}
        />
      </Sheet>

      <Sheet open={actions} onClose={() => setActions(false)} title="Add items">
        <div className="mb-sheet-actions">
          <button className="mb-btn primary block" onClick={() => { setActions(false); onAddItem?.(); }}>
            + Add a single item
          </button>
          <button className="mb-btn block" onClick={() => { setActions(false); onBulkAdd?.(); }}>
            Bulk add
          </button>
          <button className="mb-btn block" onClick={() => { setActions(false); onImportInventory?.(); }}>
            Import from Steam inventory
          </button>
          <button className="mb-btn ghost block" onClick={() => { setActions(false); onExportCSV?.(); }}>
            Export everything as CSV
          </button>
        </div>
      </Sheet>
    </Page>
  );
}
